import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserId, getUserRole } from '../userLocalDetails';

// The header credit pill (main bundle) uses this hook, so the API client is
// loaded on first use instead of with the app; the flag-off build never asks.
// The flag is tested as the literal (utils/teacherLoopGate.js), so a flag-off
// build drops the import, the query and the API client's chunk.
const fetchTeacherLoopMe = () =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('../service/teacherLoop.api').then((api) => api.getTeacherLoopMe())
    : Promise.reject(new Error('Teacher Loop is off'));

export const teacherLoopMeKey = (userId) => [
  'teacherLoop',
  'me',
  String(userId || ''),
];

// Grants already handed to the profile, as `${userId}|${unlimitedUntil}`, so the
// header pill and the draft page (two hook instances) refresh it once between them.
const syncedGrants = new Set();

/**
 * /teacher-loop/me and the profile are cached apart, and the profile never
 * refetches on mount (createGlobalState). A Teacher Check grant made while the
 * profile sat in the cache showed as unlimited in the credit pill while the
 * submit gate, which reads profile.permissions.credits, still asked for credits
 * (QA case E5). Refresh the profile once when /me reports a running grant that
 * the cached profile does not have yet.
 */
const useProfileFollowsGrant = (queryClient, userId, unlimitedUntil) => {
  useEffect(() => {
    const until = String(unlimitedUntil || '');
    const key = `${userId}|${until}`;
    if (!until || syncedGrants.has(key) || !(Date.parse(until) > Date.now())) {
      return;
    }
    const profile = queryClient.getQueryData(['profile']);
    if (profile?.permissions?.credits?.isUnlimitedCredits === true) return;
    syncedGrants.add(key);
    queryClient.invalidateQueries({
      queryKey: ['profile'],
      refetchType: 'all',
    });
  }, [queryClient, userId, unlimitedUntil]);
};

const OFF = Object.freeze({
  me: null,
  isEnabled: false,
  isLoading: false,
  refetch: () => {},
});

const useTeacherLoopMeQuery = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const isStudent = String(getUserRole() || '').toUpperCase() === 'STUDENT';
  const query = useQuery({
    queryKey: teacherLoopMeKey(userId),
    // getTeacherLoopMe resolves an object or throws, so a failure is an error
    // state (UI off), never a cached null.
    queryFn: fetchTeacherLoopMe,
    enabled: Boolean(enabled && userId && isStudent),
    staleTime: 60_000,
    cacheTime: 10 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const me = query.data && typeof query.data === 'object' ? query.data : null;
  useProfileFollowsGrant(queryClient, userId, me?.unlimitedUntil);
  return {
    me,
    isEnabled: me?.enabled === true,
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
    refetch: query.refetch,
  };
};

/**
 * GET /teacher-loop/me for the signed-in student.
 *
 * The build flag is a compile-time constant, so the early return below never
 * changes between renders of a running app: with the flag off no query hook is
 * created at all and nothing is requested. Callers must still treat
 * `isEnabled === false` as "render nothing".
 */
export function useTeacherLoopMe(options) {
  if (process.env.REACT_APP_TEACHER_LOOP !== 'on') return OFF;
  return useTeacherLoopMeQuery(options);
}

export default useTeacherLoopMe;
